/***
 * !!! Не забыть вызвать cancel в onBeforeUnmount компонента для снятия блокировки
 ***/

import { computed, ref } from 'vue';
import { type ResponseData } from '@/core/plugins/api/api';
import { useAppState } from '@/core/store/useAppState';
import { isObject } from '@/core/utils/type-guards';
import { errorTypes } from '@/parts/errors/utils/error-types';

const KEEP_ALIVE_TIMEOUT = 120; // sec
const USER_ACTIVE_TIMEOUT = 60; // sec

let resetIsUserActiveTimerId: NodeJS.Timeout;
let isUserActive = true;

const resources = ref<
  Record<
    string,
    {
      resourceId: string;
      keepAliveTimerId?: NodeJS.Timeout;
      isResourceEdit: boolean;
      // flag to prevent keepAlive while cancel resource is in progress
      isResourceCanceling: boolean;
      isEditResourceStartLoading: boolean;
      isEditResourceUpdateLoading: boolean;
    }
  >
>({});

export const useCommonLockResourceStore = () => {
  const isAnyResourceEdit = computed(() => {
    return Object.values(resources.value).some((resource) => resource.isResourceEdit);
  });

  return {
    isAnyResourceEdit,
  };
};

export const useLockResource = <
  StartEditResponse extends Record<string, unknown>,
  KeepAliveEditResponse extends Record<string, unknown>,
  CancelEditResponse extends Record<string, unknown>,
  UpdatePayload,
  UpdateResponse extends Record<string, unknown>,
>(
  resourceKey: string,
  startEditMutation: (id: string) => Promise<ResponseData<StartEditResponse>>,
  keepAliveEditMutation: (id: string) => Promise<ResponseData<KeepAliveEditResponse>>,
  cancelEditMutation: (id: string) => Promise<ResponseData<CancelEditResponse>>,
  updateMutation: (payload: UpdatePayload) => Promise<ResponseData<UpdateResponse>>,
) => {
  if (!resources.value[resourceKey]) {
    resources.value[resourceKey] = {
      resourceId: '',
      keepAliveTimerId: undefined,
      isResourceEdit: false,
      isResourceCanceling: false,
      isEditResourceStartLoading: false,
      isEditResourceUpdateLoading: false,
    };
  }

  const isResourceEdit = computed(() => resources.value[resourceKey].isResourceEdit);

  const editResourceStart = async (id: string) => {
    resources.value[resourceKey].resourceId = id;

    resources.value[resourceKey].isEditResourceStartLoading = true;

    const result = await startEditMutation(resources.value[resourceKey].resourceId);

    resources.value[resourceKey].isEditResourceStartLoading = false;

    if (!(result as { errors: unknown }).errors) {
      resources.value[resourceKey].isResourceEdit = true;

      resources.value[resourceKey].keepAliveTimerId = setInterval(() => {
        if (isUserActive && !resources.value[resourceKey].isResourceCanceling) {
          keepAliveEditMutation(resources.value[resourceKey].resourceId);
        }
      }, KEEP_ALIVE_TIMEOUT * 1000);

      window.addEventListener('beforeunload', editResourceCancel);
      initIsUserActiveChecking();
    }

    return result;
  };

  const editResourceCancel = async () => {
    if (resources.value[resourceKey].isResourceEdit) {
      resources.value[resourceKey].isResourceCanceling = true;
      await cancelEditMutation(resources.value[resourceKey].resourceId);
      clearResourceEdit();
    }
  };

  const editResourceUpdate = async (payload: UpdatePayload) => {
    resources.value[resourceKey].isEditResourceUpdateLoading = true;

    const _result = await updateMutation(payload);

    const errors = isObject(_result) ? (_result.errors as Array<{ __typename: string }> | null) : null;

    resources.value[resourceKey].isEditResourceUpdateLoading = false;

    if (!errors) {
      clearResourceEdit();
    } else if (errors[0].__typename === errorTypes.OperationOnUnlockedResourceIsInvalidError) {
      const { showReloadPageAlert } = useAppState();
      showReloadPageAlert({
        title: 'Время сессии истекло',
        subtitle:
          'Для продолжения работы необходимо обновить страницу. <br /> Данные которые вы ввели, не&nbsp;будут сохранены.',
      });
    }

    return _result;
  };

  const clearResourceEdit = () => {
    resources.value[resourceKey].isResourceEdit = false;
    resources.value[resourceKey].isResourceCanceling = false;
    clearInterval(resources.value[resourceKey].keepAliveTimerId);
    window.removeEventListener('beforeunload', editResourceCancel);
    clearIsUserActiveChecking();
  };

  const initIsUserActiveChecking = () => {
    document.addEventListener('mousemove', resetIsUserActiveTimer);
    document.addEventListener('keydown', resetIsUserActiveTimer);
    document.addEventListener('scroll', resetIsUserActiveTimer);
    document.addEventListener('click', resetIsUserActiveTimer);
    document.addEventListener('touchstart', resetIsUserActiveTimer);
    resetIsUserActiveTimer();
  };

  const clearIsUserActiveChecking = () => {
    document.removeEventListener('mousemove', resetIsUserActiveTimer);
    document.removeEventListener('keydown', resetIsUserActiveTimer);
    document.removeEventListener('scroll', resetIsUserActiveTimer);
    document.removeEventListener('click', resetIsUserActiveTimer);
    document.removeEventListener('touchstart', resetIsUserActiveTimer);
    clearTimeout(resetIsUserActiveTimerId);
  };

  const resetIsUserActiveTimer = () => {
    isUserActive = true;
    clearTimeout(resetIsUserActiveTimerId);
    resetIsUserActiveTimerId = setTimeout(() => {
      isUserActive = false;
    }, USER_ACTIVE_TIMEOUT * 1000);
  };

  return {
    isResourceEdit,
    editResourceStart,
    isEditResourceStartLoading: computed(() => resources.value[resourceKey].isEditResourceStartLoading),
    editResourceCancel,
    editResourceUpdate,
    isEditResourceUpdateLoading: computed(() => resources.value[resourceKey].isEditResourceUpdateLoading),
  };
};
